import {Trans} from '@lingui/macro';
import {omit} from 'lodash';
import React, {FC, ReactNode, useCallback, useMemo} from 'react';
import styled, {css} from 'styled-components';

import {Handler, HandlerOf, PredicateOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {blendColors} from '../../../libs/shared/ui/src/styles/colorHelpers';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {TeamAvatar} from '../../components/common/avatars/teamAvatar';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {getSettingsCompanyHomePath} from '../../store/settings/settingsMenuSelectors';
import {settingsNavigationWidth} from '../settingsCommonConstants';
import {makeSettingsUrlFromPathId, SettingsPathId} from './settingsHelpers';
import {
  SettingsMenuBlockTypesEnum,
  SettingsMenuDefinition,
  SettingsMenuItem,
  SettingsMenuItemsBlock,
  SettingsMenuSectionTypesEnum,
} from './settingsMenuCommonTypes';
import {SettingsNavItem} from './settingsNavItem';
import {SettingsNavItemHeading} from './settingsNavItemHeading';
import {SettingsNavItemTitle} from './settingsNavItemTitle';
import {SettingsNavSwitcher} from './settingsNavSwitcher';

/*
 * Props.
 */

interface SettingsMenuProps {
  className?: string;
  isGlobalAdmin: boolean;
  settingsMenuBlocks: SettingsMenuDefinition;
  isCurrentItemActive: (pathId: SettingsPathId, parameters?: SettingsMenuItem['parameters']) => boolean;
  isOnPersonalRoute: boolean;
  findIsExpanded: PredicateOf<SettingsMenuItemsBlock>;
  onBlockCollapseClick: HandlerOf<SettingsMenuItemsBlock>;
  isOnElectron: boolean;
  hasOverlay: boolean;
  maybeExitSettings: (onDiscard: Handler) => void;
}

/*
 * Style.
 */

const StyledNav = styled.nav<{$hasOverlay: boolean}>`
  width: ${settingsNavigationWidth}px;
  background-color: ${(p) => p.theme.greys.shade10};
  border-right: 1px solid ${(p) => p.theme.greys.shade30};
  position: relative;
  display: flex;
  flex-direction: column;

  & > div:not(.back-button-wrapper) {
    padding-bottom: 31px;
  }

  &::before {
    content: '';
    position: absolute;
    background: rgba(33, 33, 33, 0.85);
    width: 100%;
    height: 100%;
    z-index: 9;
    top: 0;
    left: 0;
    opacity: 0;
    pointer-events: none;
  }

  ${(p) => addOverlayStyles(p.$hasOverlay)}
`;

function addOverlayStyles(hasOverlay: boolean) {
  if (hasOverlay) {
    return css`
      border-right-color: ${(p) => blendColors(p.theme.greys.shade30, 'rgba(33,33,33,0.85)')};
      pointer-events: none;

      &::before {
        opacity: 1;
      }
    `;
  }

  return '';
}

const StyledMenuDiv = styled.div<{$isWithoutSwitcher: boolean}>`
  overflow: auto;

  & > div:not(.back-button-wrapper) {
    padding-bottom: 31px;
  }

  & > div:not(.back-button-wrapper):last-child {
    padding-bottom: 0;
  }

  & > a {
    margin-bottom: 31px;
  }

  ${(p) =>
    p.$isWithoutSwitcher &&
    css`
      padding-top: 17px;
    `}
`;

const StyledInTrialPill = styled.div`
  border-radius: 8px;
  font-size: 8px;
  font-weight: ${fontWeights.bold};
  padding: 0 4px;
  line-height: 14px;
  height: 14px;
  display: inline-block;
  vertical-align: middle;
  margin: -2px 0 0 6px;

  color: ${(p) => p.theme.palette.yellow.shade50};
  background: ${(p) => p.theme.palette.yellow.shade30};
  text-transform: uppercase;
`;

/*
 * Component.
 */

export const SettingsMenu: FC<SettingsMenuProps> = (props) => {
  const theme = useUserTheme();
  const {isCurrentItemActive, isOnPersonalRoute, findIsExpanded, onBlockCollapseClick} = props;
  const {navigateToLink} = useLinkNavigation();
  const settingsCompanyPath = useAppSelector(getSettingsCompanyHomePath);

  const onPersonalClick = useCallback(() => {
    navigateToLink(makeSettingsUrlFromPathId('settings.user-home'));
  }, [navigateToLink]);

  const onCompanyClick = useCallback(() => {
    if (settingsCompanyPath) {
      navigateToLink(settingsCompanyPath);
    }
  }, [navigateToLink, settingsCompanyPath]);

  const keyPrefixFallback = useMemo(() => (isOnPersonalRoute ? 'personal' : 'company'), [isOnPersonalRoute]);

  const renderBlockItems = (items: ReadonlyArray<SettingsMenuItem>) =>
    items.map((item) => {
      const keyPrefix = item.parameters?.namespace ?? keyPrefixFallback;
      const key = `${keyPrefix}-${item.pathId}`;

      return (
        <SettingsNavItem
          key={key}
          canHover
          href={makeSettingsUrlFromPathId(item.pathId, item.parameters)}
          isActive={isCurrentItemActive(
            item.pathId,
            omit(item.parameters, item.omittedParametersForActiveStatus || []),
          )}
        >
          <SettingsNavItemTitle isActive={false}>
            {item.name}
            {item.hasUpsell && <Icon name="diamondSmall" color={theme.palette.yellow.shade40} />}
          </SettingsNavItemTitle>
        </SettingsNavItem>
      );
    });

  const renderMenu = () => {
    const blocksArray = isOnPersonalRoute
      ? props.settingsMenuBlocks.personal
      : props.settingsMenuBlocks.company;

    return blocksArray.map((block) => {
      if (block.type === SettingsMenuBlockTypesEnum.HOME) {
        return (
          <SettingsNavItem
            key={block.pathId}
            icon={<Icon name={block.icon ?? 'homeFilled'} />}
            isHeader
            canHover
            href={makeSettingsUrlFromPathId(block.pathId)}
            isActive={isCurrentItemActive(block.pathId)}
          >
            <SettingsNavItemHeading>{block.title}</SettingsNavItemHeading>
          </SettingsNavItem>
        );
      }

      const isCollapsed = !findIsExpanded(block);

      return (
        <div key={`${block.type}-${block.sectionId}`}>
          <SettingsNavItem
            icon={makeIconNodeForFirstItemBlock(block)}
            isHeader
            canHover
            isCollapsed={isCollapsed}
            onCollapseClick={() => onBlockCollapseClick(block)}
          >
            <SettingsNavItemHeading>
              {block.title}
              {block.team?.preferences.isTrial && (
                <StyledInTrialPill>
                  <Trans id="settingsmenu-teamintrial">In trial</Trans>
                </StyledInTrialPill>
              )}
            </SettingsNavItemHeading>
          </SettingsNavItem>
          {!isCollapsed && renderBlockItems(block.items)}
        </div>
      );
    });
  };

  const shouldRenderSwitcher = props.settingsMenuBlocks.company.length > 0;

  return (
    <StyledNav className={props.className} $hasOverlay={props.hasOverlay}>
      {shouldRenderSwitcher && (
        <SettingsNavSwitcher
          isGlobalAdmin={props.isGlobalAdmin}
          selectedFilterIndex={isOnPersonalRoute ? 1 : 0}
          onPersonalClick={onPersonalClick}
          onCompanyClick={onCompanyClick}
        />
      )}
      <StyledMenuDiv $isWithoutSwitcher={!shouldRenderSwitcher}>{renderMenu()}</StyledMenuDiv>
    </StyledNav>
  );
};

function makeIconNodeForFirstItemBlock(block: SettingsMenuItemsBlock): ReactNode | undefined {
  if (block.type !== SettingsMenuBlockTypesEnum.SECTION) {
    return undefined;
  }

  if (block.team) {
    return <TeamAvatar size={VisualSizesEnum.MEDIUM} team={block.team} />;
  }

  if (block.sectionType === SettingsMenuSectionTypesEnum.PERSONAL) {
    return <Icon name="personFilled" />;
  }

  if (block.sectionType === SettingsMenuSectionTypesEnum.COMPANY) {
    return <Icon name="buildingFilled" />;
  }

  return undefined;
}
